import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { KpiStrip, MetricCard, SectionHeader, Panel, ErpTable, Th, Td, EmptyRow } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtNumber } from '@/shared/lib/format'
import {
  ChartFrame, Sparkbars, RankedBars, FunnelBars, OutcomeStrip, BucketAxis, ScatterQuadrant,
} from '@/shared/ui/charts'
import { Donut } from '@/shared/charts/Donut'
import {
  getFloor, hourlyRollup, listCells, totalRollup, type Floor, type TurnoutCell,
} from './api'
import { ElectionPicker } from './ElectionPicker'
import { useElection } from './useElection'
import { nationalOr } from './units'
import { derive, type CellView } from './analytics/model'
import { advise, ask } from './analytics/council'
import { AskPanel, CouncilBoard, type Finding } from '@/shared/ui/council'
import { GeoMap, toneOf } from './analytics/GeoMap'

/**
 * Analytics — the turnout operation, read five ways, at any hour of the day.
 *
 * ## What changed and why
 *
 * The page used to be five tiles and one bar chart over the pre-computed rollup. The
 * rollup is keyed on a geographic UNIT, so it can say a ward is behind and can never say
 * which of that ward's cells carries the shortfall — and "the ward is behind" is not a
 * decision anybody can act on. The Floor payload can: it ships every cell's cumulative
 * count for every hour of the poll. Joining it to the cell register adds the coordinates,
 * and with position in hand the same numbers answer *where*, which is the question a
 * campaign actually has on the afternoon of an election.
 *
 * Three sources, and each is here for something the others cannot do:
 *
 * - **floor** — per-cell, per-hour counts. Everything scrubbable comes from this.
 * - **register** (`listCells`) — coordinates, coordinators, meeting points. The only
 *   source of geography; the floor payload carries counts, not places.
 * - **rollup** — registered voters and incentive spend, which are unit-level facts and
 *   are not in the floor payload at all.
 *
 * ## The scrubber is the feature, not a control
 *
 * Every tab redraws at the selected hour, and the pace rule is recomputed from that hour
 * rather than read off the payload. That is what makes "was this cell always slow, or did
 * it stop at eleven?" answerable — and those two have completely different responses.
 * Because the whole day arrives in one request, dragging it costs nothing.
 */

type Tab = 'turnout' | 'geography' | 'mix' | 'cells' | 'council'

const TABS: { id: Tab; label: string; hint: string }[] = [
  { id: 'turnout',   label: 'Turnout',    hint: 'Funnel, velocity, pace' },
  { id: 'geography', label: 'Geography',  hint: 'Cells on the ground' },
  { id: 'mix',       label: 'Composition', hint: 'Where the people are' },
  { id: 'cells',     label: 'Cells',      hint: 'Ranked, sortable' },
  { id: 'council',   label: 'AI Council', hint: 'Patterns, read as decisions' },
]

export function AnalyticsPage() {
  const canAnalyse = useCan('turnout:analyse')
  const selection = useElection()
  const { electionId, election } = selection

  const [tab, setTab] = useState<Tab>('turnout')
  const [hour, setHour] = useState<number | null>(null)
  const [playing, setPlaying] = useState(false)
  const [stationId, setStationId] = useState<string | null>(null)
  const [selectedCell, setSelectedCell] = useState<string | null>(null)

  const floor = useLoad<Floor | null>(
    () => (electionId ? getFloor(electionId) : Promise.resolve(null)), [electionId])

  const register = useLoad<TurnoutCell[]>(
    () => (electionId ? listCells({ electionId }) : Promise.resolve([])), [electionId])

  const rollup = useLoad(
    () => (electionId && election
      ? nationalOr(election.scopeUnitId).then((unitId) =>
          unitId ? totalRollup(electionId, unitId) : null)
      : Promise.resolve(null)),
    [electionId, election])

  const hourlyUnit = useLoad(
    () => (electionId && election
      ? nationalOr(election.scopeUnitId).then((unitId) =>
          unitId ? hourlyRollup(electionId, unitId) : [])
      : Promise.resolve([])),
    [electionId, election])

  const f = floor.data
  const hourCount = f?.hours.length ?? 0
  // The scrubber lands on the LAST hour of a fresh payload, and resets when the election
  // changes. Left at 0 it would open every board on the first hour of polling, which
  // reads as a catastrophic morning rather than as an unset control.
  useEffect(() => { setHour(hourCount > 0 ? hourCount - 1 : null) }, [hourCount, electionId])
  useEffect(() => { setStationId(null); setSelectedCell(null) }, [electionId])

  const playRef = useRef(playing)
  playRef.current = playing
  useEffect(() => {
    if (!playing || hourCount === 0) return
    const t = setInterval(() => {
      setHour((h) => {
        const next = (h ?? 0) + 1
        // Stop at the end rather than looping. A board that silently restarts the day is
        // how somebody reads the 7am figure as the current one.
        if (next > hourCount - 1) { setPlaying(false); return hourCount - 1 }
        return next
      })
    }, 700)
    return () => clearInterval(t)
  }, [playing, hourCount])

  const d = useMemo(
    () => (f ? derive(f, register.data ?? [], hour ?? (hourCount - 1), stationId) : null),
    [f, register.data, hour, hourCount, stationId])

  const findings = useMemo(
    () => (d ? advise(d, rollup.data ?? null) : []), [d, rollup.data])

  if (!canAnalyse) return <Forbidden what="Turnout analytics" />

  const loading = floor.loading && !f
  const err = selection.error ?? floor.error ?? register.error ?? rollup.error ?? hourlyUnit.error

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Analytics"
        description="The whole polling day in one payload — every chart redraws at the hour you select, with no further requests."
        right={<ElectionPicker selection={selection} />}
      />
      <ErrorStrip message={err} />

      {loading ? <LoadingBlock label="Loading the day…" /> : !d || !f ? (
        <Panel><p className="text-erp-sm text-muted-foreground">
          No election selected, or the floor payload is unavailable for it.
        </p></Panel>
      ) : f.unavailable ? (
        <Panel title="Nothing to analyse yet">
          <p className="text-erp-sm text-muted-foreground">{f.unavailable}</p>
        </Panel>
      ) : (
        <>
          <Controls
            floor={f} d={d} hour={hour ?? 0} setHour={setHour}
            playing={playing} setPlaying={setPlaying}
            stationId={stationId} setStationId={setStationId}
          />

          <Headline d={d} registered={rollup.data?.registeredCount ?? null}
                    spend={rollup.data?.incentiveSpend ?? null}
                    currency={rollup.data?.incentiveCurrency ?? null} />

          <TabBar tab={tab} setTab={setTab} findings={findings} />

          {tab === 'turnout' && <TurnoutTab d={d} hourlyBuckets={(hourlyUnit.data ?? []).length} />}
          {tab === 'geography' && (
            <GeographyTab d={d} selected={selectedCell} onSelect={setSelectedCell} />
          )}
          {tab === 'mix' && <MixTab d={d} rollupSpend={rollup.data?.incentiveSpend ?? null}
                                    currency={rollup.data?.incentiveCurrency ?? null} />}
          {tab === 'cells' && <CellsTab d={d} />}
          {tab === 'council' && (
            <CouncilTab d={d} findings={findings} electionId={electionId ?? 'none'} />
          )}
        </>
      )}
    </div>
  )
}

// ─── controls ────────────────────────────────────────────────────────────────

function Controls({
  floor, d, hour, setHour, playing, setPlaying, stationId, setStationId,
}: {
  floor: Floor
  d: ReturnType<typeof derive>
  hour: number
  setHour: (n: number) => void
  playing: boolean
  setPlaying: (b: boolean) => void
  stationId: string | null
  setStationId: (s: string | null) => void
}) {
  const label = (iso?: string) =>
    iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'
  const at = floor.hours[d.hour]
  const isLast = d.hour >= floor.hours.length - 1

  return (
    <Panel dense bodyClassName="p-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setPlaying(!playing)}
          disabled={floor.hours.length === 0}
          className="erp-btn px-3 py-1.5 text-erp-sm rounded-md border border-border hover:bg-muted disabled:opacity-40"
          aria-label={playing ? 'Pause the replay' : 'Replay the day'}
        >
          {playing ? '❚❚ Pause' : '▶ Replay the day'}
        </button>

        <div className="flex items-center gap-2 min-w-[260px] flex-1">
          <span className="text-erp-xs text-muted-foreground shrink-0">
            {label(floor.hours[0])}
          </span>
          <input
            type="range"
            min={0}
            max={Math.max(floor.hours.length - 1, 0)}
            value={hour}
            onChange={(e) => { setPlaying(false); setHour(Number(e.target.value)) }}
            className="flex-1 accent-primary"
            aria-label="Hour of the polling day"
          />
          <span className="text-erp-xs text-muted-foreground shrink-0">
            {label(floor.hours[floor.hours.length - 1])}
          </span>
        </div>

        <div className="text-erp-sm tabular-nums font-medium shrink-0">
          {label(at)}
          <span className="text-muted-foreground font-normal">
            {' '}· {isLast ? 'latest' : `hour ${d.hour + 1} of ${floor.hours.length}`}
          </span>
        </div>

        <select
          value={stationId ?? ''}
          onChange={(e) => setStationId(e.target.value || null)}
          className="erp-input text-erp-sm rounded-md border border-border bg-background px-2 py-1.5"
          aria-label="Filter by station"
        >
          <option value="">All stations</option>
          {floor.stations.map((s) => (
            <option key={s.id} value={s.id}>{s.name ?? 'Unnamed station'}</option>
          ))}
        </select>
      </div>
      {/* The board is drawn at the LAST REPORT by default, not the wall clock. A screen
          showing "now" over a forty-minute-old fact draws a reporting blackout as calm. */}
      <p className="mt-2 text-erp-xs text-muted-foreground">
        Newest report {floor.day.lastReportAt
          ? new Date(floor.day.lastReportAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : 'never'}
        {floor.day.pollsOpen
          ? ` · polls close in ${floor.day.minutesToClose} min`
          : ' · polls are closed'}
        {stationId && ' · filtered to one station; every figure below is that station only'}
      </p>
    </Panel>
  )
}

function TabBar({ tab, setTab, findings }: {
  tab: Tab; setTab: (t: Tab) => void; findings: Finding[]
}) {
  const urgent = findings.filter((f) => f.severity === 'critical').length
  return (
    <div className="flex flex-wrap gap-1 border-b border-border" role="tablist">
      {TABS.map((t) => (
        <button
          key={t.id}
          role="tab"
          aria-selected={tab === t.id}
          onClick={() => setTab(t.id)}
          title={t.hint}
          className={cn(
            'px-3 py-2 text-erp-sm rounded-t-md border-b-2 -mb-px transition-colors',
            tab === t.id
              ? 'border-primary text-foreground font-medium'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50',
          )}
        >
          {t.label}
          {t.id === 'council' && urgent > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-rose-600 text-white text-erp-xs px-1.5 min-w-[18px] h-[18px] tabular-nums">
              {urgent}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

// ─── headline ────────────────────────────────────────────────────────────────

function Headline({ d, registered, spend, currency }: {
  d: ReturnType<typeof derive>
  registered: number | null
  spend: string | number | null
  currency: string | null
}) {
  const t = d.totals
  const pct = (n: number) => `${Math.round(n * 100)}%`
  const gap = Math.round(t.members * t.paceShare) - t.voted
  const cur = currency ?? 'KES'
  const spendN = Number(spend ?? 0)

  return (
    <KpiStrip>
      <MetricCard
        label="Turnout of members" value={pct(t.share)}
        sub={<span className="tabular-nums">{fmtNumber(t.voted)} of {fmtNumber(t.members)}</span>}
      />
      {/* Once the polls have shut there is no clock to be behind, and a card still
          reporting a deficit against a pace rule reads as a live emergency hours after
          the thing it measured stopped existing. The same shortfall becomes the result. */}
      {d.closed ? (
        <MetricCard
          label="Did not vote" value={fmtNumber(t.members - t.voted)}
          sub="members who never turned out" accent={t.members - t.voted > 0}
        />
      ) : (
        <MetricCard
          label="Against the clock"
          value={gap > 0 ? `−${fmtNumber(gap)}` : `+${fmtNumber(-gap)}`}
          accent={gap > 0}
          sub={`pace says ${pct(t.paceShare)} by now`}
          deltaType={gap > 0 ? 'down' : 'up'}
        />
      )}
      <MetricCard
        label="Still to move" value={fmtNumber(t.outstanding)}
        sub={<span>{fmtNumber(t.inQueue)} queuing · {fmtNumber(t.enRoute)} en route</span>}
      />
      {/* At the close every cell under 100% is "behind pace", which would put the whole
          board in this card and make it useless. The finished-under-half count is the
          post-poll equivalent: the cells that actually failed. */}
      {d.closed ? (
        <MetricCard
          label="Cells that under-delivered"
          value={fmtNumber(d.cells.filter((c) => c.share < 0.5).length)}
          sub={`of ${d.cells.length} · finished under half their members`}
          accent={d.cells.some((c) => c.share < 0.5)}
        />
      ) : (
        <MetricCard
          label="Cells needing attention" value={fmtNumber(t.cellsBehind + t.cellsStalled)}
          sub={`${t.cellsBehind} behind pace · ${t.cellsStalled} silent`}
          accent={t.cellsStalled > 0}
        />
      )}
      <MetricCard
        label="Cost per vote"
        value={spendN > 0 && t.voted > 0 ? `${cur} ${fmtNumber(Math.round(spendN / t.voted))}` : '—'}
        sub={spendN > 0 ? `${cur} ${fmtNumber(spendN)} spent` : 'no incentive budget drawn'}
      />
      <MetricCard
        label="Share of the register"
        value={registered && registered > 0 ? pct(t.voted / registered) : '—'}
        sub={registered ? `${fmtNumber(registered)} registered in scope` : 'register not counted'}
      />
    </KpiStrip>
  )
}

// ─── tab: turnout ────────────────────────────────────────────────────────────

function TurnoutTab({ d, hourlyBuckets }: {
  d: ReturnType<typeof derive>; hourlyBuckets: number
}) {
  const t = d.totals
  const hour = (iso: string) =>
    new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  const velocity = d.beats.map((b) => ({
    label: hour(b.at), value: b.votedInHour, hint: `${fmtNumber(b.cumulative)} cumulative`,
  }))
  const cumulative = d.beats.map((b) => ({
    label: hour(b.at), value: b.cumulative, hint: `pace line ${fmtNumber(b.requiredPace)}`,
  }))
  const pace = d.beats.map((b) => ({ label: hour(b.at), value: b.requiredPace }))

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title="The funnel"
             description="Where the operation loses people, stage by stage.">
        <FunnelBars stages={[
          { label: 'Members enrolled', value: t.members, hint: 'in cells' },
          { label: 'Pledged to vote', value: t.pledged, hint: 'said yes to a coordinator' },
          { label: 'Moved (queue or en route)', value: t.inQueue + t.enRoute + t.voted, hint: 'left home' },
          { label: 'Voted', value: t.voted, hint: 'recorded at a station' },
        ]} />
        <p className="mt-3 text-erp-xs text-muted-foreground">
          The drop from pledge to vote is the only one you can still close today — a pledge is
          somebody who already agreed once, so the call is a reminder rather than a persuasion.
        </p>
      </Panel>

      <Panel title="Where everybody is, right now"
             description="One bar, four states. The whole membership, accounted for.">
        <OutcomeStrip parts={[
          { label: 'Voted', value: t.voted, tone: 'good' },
          { label: 'In the queue', value: t.inQueue, tone: 'warn' },
          { label: 'En route', value: t.enRoute, tone: 'neutral' },
          { label: 'Not moved', value: Math.max(t.members - t.voted - t.inQueue - t.enRoute, 0), tone: 'bad' },
        ]} />
        <div className="mt-4 grid grid-cols-2 gap-3 text-erp-sm">
          <Fact label="Cells reporting" value={`${d.cells.length - t.cellsStalled} of ${d.cells.length}`} />
          <Fact label={d.closed ? 'Cells under half' : 'Cells behind pace'}
                value={fmtNumber(d.closed ? d.cells.filter((c) => c.share < 0.5).length : t.cellsBehind)} />
          <Fact label="Unmatched transport" value={fmtNumber(t.transportGaps)} />
          <Fact label="Escalations past due" value={fmtNumber(t.escalationsLate)} />
        </div>
      </Panel>

      <Panel title="Velocity — votes in each hour"
             description="The hour's own count. A cumulative curve always rises and hides a stall.">
        <ChartFrame title="Votes per hour">
          <Sparkbars points={velocity} height={140}
                     emptyMessage="No hourly buckets yet — the rollup writes them on polling day." />
          <BucketAxis points={velocity} />
        </ChartFrame>
      </Panel>

      <Panel title="Cumulative against the pace line"
             description="Two panels sharing one x axis rather than two lines on one — the codebase's rule, and here it is also the honest reading.">
        <div className="space-y-4">
          <ChartFrame title="Actual, cumulative">
            <Sparkbars points={cumulative} height={100} emptyMessage="Nothing recorded yet." />
          </ChartFrame>
          <ChartFrame title="Required, to finish on time">
            <Sparkbars points={pace} height={100} emptyMessage="No pace line — the poll window is unset." />
            <BucketAxis points={pace} />
          </ChartFrame>
        </div>
        <p className="mt-3 text-erp-xs text-muted-foreground">
          Read the two panels' heights against their own labelled peaks, not against each other.
          {hourlyBuckets > 0 && ` The unit rollup holds ${hourlyBuckets} hourly buckets for the same window.`}
        </p>
      </Panel>
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-border/60 pb-1">
      <span className="text-erp-xs text-muted-foreground">{label}</span>
      <span className="tabular-nums font-medium">{value}</span>
    </div>
  )
}

// ─── tab: geography ──────────────────────────────────────────────────────────

function GeographyTab({ d, selected, onSelect }: {
  d: ReturnType<typeof derive>; selected: string | null; onSelect: (id: string | null) => void
}) {
  const chosen = d.cells.find((c) => c.id === selected) ?? null
  return (
    <div className="space-y-4">
      <Panel title="Cells on the ground"
             description="Position from the cell's own centroid. Colour is state; size is how many people the cell holds.">
        <GeoMap cells={d.cells} selected={selected} onSelect={onSelect} closed={d.closed} />
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Stations by outstanding voters"
               description="Who is left, by station — the list a fleet dispatcher works from.">
          <RankedBars
            points={[...d.stations]
              .sort((a, b) => (b.members - b.voted) - (a.members - a.voted))
              .slice(0, 10)
              .map((s) => ({
                label: s.name,
                value: s.members - s.voted,
                hint: `${Math.round(s.share * 100)}% turned out · ${s.cells} cells`,
              }))}
            tone={(label) => {
              const s = d.stations.find((x) => x.name === label)
              if (!s) return undefined
              return s.behindCells > s.cells / 2 ? 'bad' : s.share > d.totals.paceShare ? 'good' : 'warn'
            }}
            emptyMessage="No stations in view."
          />
        </Panel>

        <Panel title={chosen ? `${chosen.code} — the detail` : 'Pick a cell on the map'}
               description={chosen ? (chosen.stationName ?? 'Unnamed station') : 'Its hour-by-hour curve appears here.'}>
          {chosen ? <CellDetail cell={chosen} /> : (
            <p className="text-erp-sm text-muted-foreground">
              Click any bubble. A cell that is behind and a cell that has gone quiet look
              similar in a total and need opposite responses, so the curve is worth reading
              before dispatching anything.
            </p>
          )}
        </Panel>
      </div>
    </div>
  )
}

function CellDetail({ cell }: { cell: CellView }) {
  const points = cell.series.map((v, i) => ({ label: `${i + 1}`, value: v }))
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 text-erp-sm">
        <Fact label="Members" value={fmtNumber(cell.members)} />
        <Fact label="Voted" value={`${fmtNumber(cell.voted)} (${Math.round(cell.share * 100)}%)`} />
        <Fact label="Pledged" value={fmtNumber(cell.pledged)} />
        <Fact label="Outstanding" value={fmtNumber(cell.outstanding)} />
        <Fact label="In the queue" value={fmtNumber(cell.inQueue)} />
        <Fact label="Transport gaps" value={fmtNumber(cell.transportGaps)} />
        <Fact label="Coordinator" value={cell.coordinatorName ?? 'none appointed'} />
        <Fact label="Last report"
              value={cell.stalledMinutes == null ? 'never' : `${cell.stalledMinutes} min ago`} />
      </div>
      <ChartFrame title="Cumulative, hour by hour">
        <Sparkbars points={points} height={90} emptyMessage="No series for this cell." />
      </ChartFrame>
      {cell.meetingPoint && (
        <p className="text-erp-xs text-muted-foreground">Meets at {cell.meetingPoint}.</p>
      )}
      {cell.perHourNeeded != null && (
        <p className="text-erp-sm">
          Needs <span className="font-medium tabular-nums">{cell.perHourNeeded}</span> people an
          hour to finish before the close.
        </p>
      )}
    </div>
  )
}

// ─── tab: composition ────────────────────────────────────────────────────────

function MixTab({ d, rollupSpend, currency }: {
  d: ReturnType<typeof derive>; rollupSpend: string | number | null; currency: string | null
}) {
  const t = d.totals
  const notMoved = Math.max(t.members - t.voted - t.inQueue - t.enRoute, 0)

  const statusCounts = new Map<string, number>()
  for (const c of d.cells) statusCounts.set(c.statusCode, (statusCounts.get(c.statusCode) ?? 0) + 1)

  const toneCounts = new Map<string, number>()
  for (const c of d.cells) {
    const k = toneOf(c, d.closed)
    toneCounts.set(k, (toneCounts.get(k) ?? 0) + 1)
  }
  // Worst first, so the ordinal ramp's dark end is the end a reader scans to. The labels
  // change at the close because the categories do — see `toneOf`.
  const TONE_ORDER = ['ahead', 'onpace', 'behind', 'silent'] as const
  const TONE_LABEL: Record<string, string> = d.closed
    ? { ahead: 'Finished above 75%', onpace: 'Finished 50–75%', behind: 'Finished under 50%', silent: 'Never reported' }
    : { ahead: 'Ahead of pace', onpace: 'At pace', behind: 'Behind pace', silent: 'Silent 90+ min' }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title="Every member, by state"
             description="The part-to-whole the whole operation is judged on.">
        <Donut
          slices={[
            { id: 'voted', label: 'Voted', value: t.voted },
            { id: 'queue', label: 'In the queue', value: t.inQueue },
            { id: 'route', label: 'En route', value: t.enRoute },
            { id: 'still', label: 'Not moved', value: notMoved },
          ]}
          total={t.members}
          centreLabel="Members"
          emptyMessage="No members enrolled in the cells in view, so there is no whole to divide."
        />
      </Panel>

      <Panel title="Cells by how they are doing"
             description="An ordered scale, drawn on the ordinal ramp — position in the ring is the rank.">
        <Donut
          palette="ordinal"
          slices={TONE_ORDER.map((k) => ({
            id: k, label: TONE_LABEL[k]!, value: toneCounts.get(k) ?? 0,
          }))}
          total={d.cells.length}
          centreLabel="Cells"
          formatValue={(v) => `${Math.round(v)}`}
          emptyMessage="No cells in view."
        />
      </Panel>

      <Panel title="Members by station"
             description="Where your organisation actually is, as opposed to where it is winning.">
        <Donut
          slices={d.stations.map((s) => ({ id: s.id, label: s.name, value: s.members }))}
          total={t.members}
          centreLabel="Members"
          emptyMessage="No stations in view."
        />
      </Panel>

      <Panel title="Cell register"
             description="Status as recorded, not as inferred from activity — the two disagreeing is itself a finding.">
        <Donut
          slices={[...statusCounts].map(([k, v]) => ({ id: k, label: k, value: v }))}
          total={d.cells.length}
          centreLabel="Cells"
          formatValue={(v) => `${Math.round(v)}`}
          emptyMessage="No cells in view."
        />
        <div className="mt-4 space-y-1 text-erp-xs text-muted-foreground">
          <p>{t.cellsNoCoordinator} of {d.cells.length} cells have no coordinator appointed.</p>
          <p>{t.cellsWithoutCoords} carry no coordinate and cannot be placed on the map.</p>
          {Number(rollupSpend ?? 0) > 0 && (
            <p>
              {currency ?? 'KES'} {fmtNumber(Number(rollupSpend))} of incentive spend sits behind
              these cells.
            </p>
          )}
        </div>
      </Panel>
    </div>
  )
}

// ─── tab: cells ──────────────────────────────────────────────────────────────

type SortKey = 'behind' | 'outstanding' | 'share' | 'members' | 'queue' | 'silent'

function CellsTab({ d }: { d: ReturnType<typeof derive> }) {
  const navigate = useNavigate()
  const [sort, setSort] = useState<SortKey>('behind')
  const [minMembers, setMinMembers] = useState(0)

  const rows = useMemo(() => {
    const f = d.cells.filter((c) => c.members >= minMembers)
    const by: Record<SortKey, (a: CellView, b: CellView) => number> = {
      behind: (a, b) => b.behind - a.behind,
      outstanding: (a, b) => b.outstanding - a.outstanding,
      share: (a, b) => a.share - b.share,
      members: (a, b) => b.members - a.members,
      queue: (a, b) => b.inQueue - a.inQueue,
      silent: (a, b) => (b.stalledMinutes ?? -1) - (a.stalledMinutes ?? -1),
    }
    return [...f].sort(by[sort])
  }, [d.cells, sort, minMembers])

  return (
    <div className="space-y-4">
      {/*
         The x axis changes at the close of poll, because the useful question does.

         While the polls are open, pace against achievement is the triage: bottom-right is
         a cell that should be far along and is not. Once they shut, EVERY cell shares the
         same pace of 100% — the plot collapses to a vertical line at the right edge and
         says nothing at all. After the close the question is whether pledging predicted
         voting, which is the finding that changes how the next campaign is run.
      */}
      <Panel
        title={d.closed ? 'Did the pledges hold?' : 'Pace against achievement'}
        description={d.closed
          ? 'Each cell: what share pledged, against what share actually voted. Cells far below the middle promised and did not deliver.'
          : 'Bottom-right is the emergency: a cell that should be far along and is not. Bubble area is cell size.'}
      >
        <ScatterQuadrant
          xLabel={d.closed ? 'Pledged (% of members)' : 'Where it should be (pace %)'}
          yLabel={d.closed ? 'Voted (% of members)' : 'Where it is (turnout %)'}
          height={340}
          onSelect={(id) => navigate(`/station-mates/cells?cell=${id}`)}
          points={d.cells.map((c) => ({
            id: c.id,
            label: c.code,
            x: d.closed
              ? (c.members === 0 ? null : Math.round((c.pledged / c.members) * 100))
              : Math.round(c.paceShare * 100),
            y: c.members === 0 ? null : Math.round(c.share * 100),
            size: c.members,
            tone: (c.stalledMinutes ?? 0) >= 90 && !d.closed ? 'warn'
              : d.closed
                ? (c.share >= 0.75 ? 'ok' : c.share >= 0.5 ? 'muted' : 'danger')
                : c.behind > 0.15 ? 'danger'
                : c.share >= c.paceShare ? 'ok' : 'muted',
          }))}
        />
      </Panel>

      <Panel
        title="Every cell"
        description="Sorted by whichever column you are working from."
        right={
          <div className="flex items-center gap-2">
            <label className="text-erp-xs text-muted-foreground">Min members</label>
            <input type="number" min={0} value={minMembers}
                   onChange={(e) => setMinMembers(Math.max(0, Number(e.target.value)))}
                   className="w-16 rounded-md border border-border bg-background px-2 py-1 text-erp-sm" />
          </div>
        }
        bodyClassName="p-0"
      >
        {/* `head` is spliced INTO ErpTable's own <tr>, so it must be the cells and not a
            row. A nested <tr> is hoisted out by the parser and the header stops lining up
            with the body — which looks like a styling bug and is a markup one. */}
        <ErpTable head={
          <>
            <Th>Cell</Th>
            <Th>Station</Th>
            <SortHead k="members" sort={sort} set={setSort}>Members</SortHead>
            <SortHead k="share" sort={sort} set={setSort}>Turnout</SortHead>
            <SortHead k="behind" sort={sort} set={setSort}>Vs pace</SortHead>
            <SortHead k="outstanding" sort={sort} set={setSort}>Left</SortHead>
            <SortHead k="queue" sort={sort} set={setSort}>Queue</SortHead>
            <SortHead k="silent" sort={sort} set={setSort}>Last report</SortHead>
            <Th>Needs/hr</Th>
          </>
        }>
          {rows.length === 0 ? <EmptyRow colSpan={9} message="No cells match this filter." /> : (
            rows.map((c) => (
              <tr key={c.id} className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/station-mates/cells?cell=${c.id}`)}>
                <Td>
                  <span className="font-medium">{c.code}</span>
                  {!c.coordinatorName && (
                    <span className="ml-1.5 text-erp-xs text-amber-600 dark:text-amber-400">
                      no coordinator
                    </span>
                  )}
                </Td>
                <Td>{c.stationName ?? '—'}</Td>
                <Td className="tabular-nums">{fmtNumber(c.members)}</Td>
                <Td className="tabular-nums">{Math.round(c.share * 100)}%</Td>
                <Td className={cn('tabular-nums',
                  c.behind > 0.15 ? 'text-rose-600 dark:text-rose-400 font-medium'
                  : c.behind <= 0 ? 'text-emerald-600 dark:text-emerald-400' : '')}>
                  {c.behind > 0 ? '−' : '+'}{Math.abs(Math.round(c.behind * 100))} pts
                </Td>
                <Td className="tabular-nums">{fmtNumber(c.outstanding)}</Td>
                <Td className="tabular-nums">{c.inQueue > 0 ? fmtNumber(c.inQueue) : '—'}</Td>
                <Td className="tabular-nums">
                  {c.stalledMinutes == null ? <span className="text-muted-foreground">never</span>
                    : c.stalledMinutes >= 90
                      ? <span className="text-amber-600 dark:text-amber-400">{sinceLabel(c.stalledMinutes)}</span>
                      : sinceLabel(c.stalledMinutes)}
                </Td>
                <Td className="tabular-nums">{c.perHourNeeded ?? '—'}</Td>
              </tr>
            ))
          )}
        </ErpTable>
      </Panel>
    </div>
  )
}

/**
 * How long ago, in a unit somebody can read at a glance.
 *
 * A cell carried over from an earlier election reports "493330 min", which is a true
 * number nobody can parse into "eleven months". Minutes are right for the only window
 * that matters on polling day and wrong for everything past it.
 */
function sinceLabel(minutes: number): string {
  if (minutes < 120) return `${minutes} min`
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h`
  return `${Math.round(minutes / (60 * 24))} d`
}

function SortHead({ k, sort, set, children }: {
  k: SortKey; sort: SortKey; set: (k: SortKey) => void; children: React.ReactNode
}) {
  return (
    <Th>
      <button type="button" onClick={() => set(k)}
              className={cn('hover:text-foreground', sort === k && 'text-foreground font-semibold')}>
        {children}{sort === k && ' ▾'}
      </button>
    </Th>
  )
}

// ─── tab: AI council ─────────────────────────────────────────────────────────

/**
 * The board, written out for the model.
 *
 * The `ASK` playbook assembles no brief of its own — its user prompt is literally the question
 * — so this text IS everything the council knows about the situation. Without it, "where
 * should I send the vans?" gets answered in generalities that read like insight and are not.
 *
 * The cell list is capped at the twelve worst. A model handed three hundred rows answers about
 * the list rather than about the decision, and those twelve are the ones somebody would
 * actually dispatch against.
 */
function turnoutContext(d: ReturnType<typeof derive>, findings: Finding[]): string {
  const t = d.totals
  const pct = (n: number) => `${Math.round(n * 100)}%`
  const at = d.hours[d.hour]
  const worst = [...d.cells]
    .sort((a, b) => b.behind * b.outstanding - a.behind * a.outstanding)
    .slice(0, 12)

  return [
    "You are advising a Kenyan campaign's turnout operation. Answer in plain language a",
    'campaign manager can act on. Use ONLY the figures below; where they do not support an',
    'answer, say so rather than estimating.',
    '',
    `TIME: drawn at ${at ? new Date(at).toLocaleString() : 'an unknown hour'} — hour `
      + `${d.hour + 1} of ${d.hours.length}. Polls are ${d.closed ? 'CLOSED' : 'OPEN'}.`,
    `TOTALS: ${t.members} members in cells, ${t.voted} voted (${pct(t.share)}), `
      + `${t.outstanding} outstanding, ${t.inQueue} in a queue, ${t.enRoute} en route, `
      + `${t.pledged} pledged.`,
    `PACE: the rule says ${pct(t.paceShare)} by now, so the operation is `
      + `${Math.round(t.members * t.paceShare) - t.voted} votes behind the clock.`,
    `CELLS: ${d.cells.length} total, ${t.cellsBehind} behind pace, ${t.cellsStalled} silent for `
      + `90+ minutes, ${t.cellsNoCoordinator} with no coordinator, ${t.transportGaps} unmatched `
      + `transport requests, ${t.escalationsOpen} open escalations (${t.escalationsLate} past due).`,
    `STATIONS: ${d.stations.map((st) => `${st.name} ${st.voted}/${st.members} (${pct(st.share)})`).join('; ')}`,
    '',
    'WORST CELLS (code, station, voted/members, points behind pace, outstanding, last report):',
    ...worst.map((c) => `- ${c.code}, ${c.stationName ?? 'unknown station'}, `
      + `${c.voted}/${c.members}, ${Math.round(c.behind * 100)} pts behind, `
      + `${c.outstanding} left, `
      + `${c.stalledMinutes == null ? 'never reported' : `${c.stalledMinutes} min ago`}`
      + `${c.coordinatorName ? '' : ', NO COORDINATOR'}`),
    '',
    'WHAT OUR OWN RULES ALREADY FLAGGED (agree, refine or disagree):',
    ...findings.map((f) => `- [${f.severity}] ${f.headline} ${f.evidence}`),
  ].join('\n')
}

/**
 * The council, and the record of what was asked of it.
 *
 * Board and ask box both come from `shared/ui/council`; only the RULES are local. A
 * turnout finding and a polling finding are not the same kind of claim, and forcing one
 * vocabulary onto both would produce advice generic enough to be useless — but the shape
 * (ranked claims, each with its evidence and its move) is the same, and so is the ask
 * history, so those are shared.
 */
function CouncilTab({ d, findings, electionId }: {
  d: ReturnType<typeof derive>; findings: Finding[]; electionId: string
}) {
  const at = new Date(d.hours[d.hour] ?? Date.now())
    .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <CouncilBoard
          findings={findings}
          title="What the numbers are saying"
          description={`${findings.length} ${findings.length === 1 ? 'finding' : 'findings'} at ${at}, ranked by the votes each is worth.`}
          stakeUnit="vote"
          emptyMessage="No threshold has been crossed at this hour. That is a finding in itself — the council stays quiet rather than manufacturing advice."
        />
      </div>
      <AskPanel
        storageKey={`sm-council-history:${electionId}`}
        description="Plain language. The board's figures go with the question, so the answer is about this hour and these cells."
        placeholder="Where should I send the vans?"
        scopeNote="It sees the turnout figures on this page, not the voter register."
        suggestions={[
          'Where should I send the vans?', 'Who should we be calling?', 'Are we on track?',
          'Which cells have gone quiet?', 'What is this costing?',
        ]}
        onAsk={(q) => ask(q, findings)}
        context={() => turnoutContext(d, findings)}
      />
    </div>
  )
}
